import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from 'antd';
import { SyncOutlined, SettingOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import {
  getEstado,
  getRitmo,
  huellaHistorico,
  type BrevoSyncEstadoResponse,
  type BrevoSyncRitmoPunto,
} from '../api/brevoSyncApi';
import StatusBanner from '../components/BrevoSync/StatusBanner';
import PhaseProgress from '../components/BrevoSync/PhaseProgress';
import StatCards from '../components/BrevoSync/StatCards';
import HistorySection from '../components/BrevoSync/HistorySection';
import LaunchModal from '../components/BrevoSync/LaunchModal';
import GcPagina from '../components/Layout/GcPagina';
import { FctTarjeta } from '../fct/react';
import { fct } from '../fct/tokens/tokens';

const BrevoSyncPage: React.FC = () => {
  const [estado, setEstado] = useState<BrevoSyncEstadoResponse | null>(null);
  const [ritmo, setRitmo] = useState<BrevoSyncRitmoPunto[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = useNavigate();

  const isRunning = !!estado?.running;

  const fetchEstado = useCallback(async () => {
    try {
      const data = await getEstado();
      setEstado(data);
      setLoading(false);

      // Fetch ritmo si hay ejecución en curso
      if (data.running) {
        try {
          const ritmoData = await getRitmo(data.running.id, 'A');
          setRitmo(ritmoData.puntos);
        } catch { /* silently fail */ }
      }
    } catch {
      setLoading(false);
    }
  }, []);

  // Polling
  useEffect(() => {
    fetchEstado();

    const setupInterval = () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      const delay = isRunning ? 5000 : 60000;
      intervalRef.current = setInterval(fetchEstado, delay);
    };

    setupInterval();

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isRunning, fetchEstado]);

  // Pausa polling cuando la pestaña pierde foco
  useEffect(() => {
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchEstado();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [fetchEstado]);

  const handleLaunched = () => {
    // Polling agresivo los primeros 30s para detectar el running lo antes posible
    let attempts = 0;
    const poll = () => {
      fetchEstado();
      attempts++;
      if (attempts < 15) setTimeout(poll, 2000);
    };
    setTimeout(poll, 1000);
  };

  return (
    <GcPagina
      seccion="Brevo"
      titulo="Sincronización Gescom y Brevo"
      contexto="Sincronización de contactos con Brevo"
      acciones={
        <>
          <Button
            type="text"
            icon={<SettingOutlined />}
            onClick={() => navigate('/brevo-sync/sistemas')}
            size="small"
            style={{ color: fct.tinta['700'] }}
            aria-label="Panel de Sistemas"
          />
          <Button
            type="primary"
            icon={<SyncOutlined />}
            onClick={() => setModalVisible(true)}
            disabled={isRunning}
          >
            Lanzar sincronización
          </Button>
        </>
      }
    >
      {/* Banner de estado */}
      <StatusBanner estado={estado} ritmo={ritmo} loading={loading} />

      <FctTarjeta titulo="Estado de la sincronización">
        {/* Progreso por fases (solo si running) */}
        {estado?.running && <PhaseProgress ejecucion={estado.running} />}

        {/* Cards de métricas */}
        <StatCards
          ejecucion={estado?.running}
          ultimaFinalizada={estado?.ultimaFinalizada}
          ritmo={ritmo}
        />
      </FctTarjeta>

      {/* Histórico */}
      <FctTarjeta style={{ marginTop: 16 }}>
        <HistorySection huella={huellaHistorico(estado)} />
      </FctTarjeta>

      {/* Modal de lanzamiento */}
      <LaunchModal
        visible={modalVisible}
        hasRunning={isRunning}
        onClose={() => setModalVisible(false)}
        onLaunched={handleLaunched}
      />
    </GcPagina>
  );
};

export default BrevoSyncPage;
